import type { AntibodyResponse } from '../types';
import type { ClonotypeRow } from '../types/antibodyTable';

/**
 * Transforms a raw AntibodyResponse into the flat ClonotypeRow shape
 * consumed by the outer table in OuterParentClonotypeTable.
 *
 * - Picks the representative sequence (highest APS) for V-J gene and CDR3 display.
 * - Aggregates barcodes across all sequences for clonotypeSize.
 */
export function deriveClonotypeRow(clonotype: AntibodyResponse): ClonotypeRow {
  const { id, name, sequences } = clonotype;

  const sorted = [...sequences].sort((a, b) => (b.metadata.aps ?? 0) - (a.metadata.aps ?? 0));
  const representative = sorted[0];

  const clonotypeSize = sequences.reduce((sum, seq) => sum + seq.metadata.barcodes.length, 0);
  const numberOfNodes = sequences.length;
  // Ig isotype lives on the heavy chain only (e.g. IgG, IgM, IgA).
  // light_chain_type is always None on the backend so we don't fall back to lightChain.
  const igIsotype = representative?.heavyChain?.igIsotype ?? null;
  const igIsotypeVariants = 0;

  const heavyV = representative?.heavyChain?.vGene ?? '';
  const heavyJ = representative?.heavyChain?.jGene ?? '';
  const vjGeneHeavy = heavyV && heavyJ ? `${heavyV}/${heavyJ}` : heavyV || heavyJ || '-';

  const lightV = representative?.lightChain?.vGene ?? '';
  const lightJ = representative?.lightChain?.jGene ?? '';
  const vjGeneLight = lightV && lightJ ? `${lightV}/${lightJ}` : lightV || lightJ || '-';

  const representativeCDRH3 = representative?.heavyChain?.regions?.cdr3 ?? '-';
  const representativeCDRL3 = representative?.lightChain?.regions?.cdr3 ?? '-';

  return {
    clonotypeId: id,
    clonotypeName: name,
    clonotypeSize,
    numberOfNodes,
    igIsotype,
    igIsotypeVariants,
    vjGeneHeavy,
    vjGeneLight,
    representativeCDRH3,
    representativeCDRL3,
    sequences,
    heavyRegions: clonotype.heavyRegions ?? [],
    lightRegions: clonotype.lightRegions ?? null,
  };
}
